import { useState, useEffect } from 'react'
import { API_BASE } from '../lib/supabase'

const VIEWS = [
  { key: 'prior', label: 'Prior Day' },
  { key: 'pre', label: 'Pre-Market' },
  { key: 'market', label: 'Market' },
  { key: 'post', label: 'Post-Market' },
]

const WINDOW_LABEL = {
  pre_market: 'Before Open',
  market: 'Market Hours',
  post_market: 'After Close',
}

export default function Dashboard() {
  const [view, setView] = useState('market')
  const [stocks, setStocks] = useState([])
  const [loading, setLoading] = useState(true)
  const [updatedAt, setUpdatedAt] = useState(null)
  const [window, setWindow] = useState(null)
  const [sortKey, setSortKey] = useState('change')
  const [sortDir, setSortDir] = useState('desc')
  const [consensus, setConsensus] = useState([])

  const fetchConsensus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/consensus-today`)
      const data = await res.json()
      setConsensus(data.picks || [])
    } catch (e) { console.error(e) }
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/earnings?view=${view}`)
      const data = await res.json()
      setStocks(data.stocks || [])
      setUpdatedAt(data.updated_at)
      setWindow(data.window)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  useEffect(() => { document.title = 'EarningsEdge — Stock Earnings Movers' }, [])
  useEffect(() => { fetchData() }, [view])
  useEffect(() => { fetchConsensus() }, [])

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const sorted = [...stocks].sort((a, b) => {
    let av = sortKey === 'mkt_cap' ? (a.mkt_cap_raw || 0) : a[sortKey]
    let bv = sortKey === 'mkt_cap' ? (b.mkt_cap_raw || 0) : b[sortKey]
    if (av === null) return 1
    if (bv === null) return -1
    if (typeof av === 'string') av = av.toLowerCase()
    if (typeof bv === 'string') bv = bv.toLowerCase()
    return sortDir === 'asc' ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1)
  })

  const SortArrow = ({ col }) => sortKey !== col
    ? <span style={{ color: 'var(--border2)', marginLeft: 4 }}>⇅</span>
    : <span style={{ color: 'var(--olive)', marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>

  return (
    <div className="animate-in">
      <style>{`
        @media (max-width: 600px) {
          .dash-header { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; margin-bottom: 14px !important; }
          .dash-title { font-size: 26px !important; }
          .dash-tabs button { padding: 8px 12px !important; font-size: 12px !important; }
          .dash-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .dash-table td, .dash-table th { padding: 10px 10px !important; }
          /* all columns visible — table scrolls horizontally on mobile */
        }
      `}</style>

      {/* Header */}
      <div className="dash-header" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
            <h1 className="dash-title" style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>
              Earnings Movers
            </h1>
            {window && (
              <span style={{
                fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '1.5px',
                textTransform: 'uppercase', color: 'var(--olive)',
                background: 'var(--olive-bg)', border: '1px solid var(--olive-dim)',
                padding: '3px 8px', borderRadius: 2,
              }}>{WINDOW_LABEL[window]}</span>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
            {updatedAt ? `Updated ${updatedAt}` : 'Loading...'}
          </p>
        </div>
        <button onClick={fetchData} style={{
          background: 'transparent', border: '1px solid var(--border2)',
          color: 'var(--text2)', padding: '7px 16px', borderRadius: 4,
          fontSize: 12, cursor: 'pointer', fontFamily: 'var(--mono)',
          letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        }}>↻ Refresh</button>
      </div>

      {/* AI Consensus Picks */}
      {consensus.length > 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderTop: '3px solid var(--olive)', borderRadius: 6,
          padding: '16px 20px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 12 }}>
            AI Consensus Picks · Today
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['ai consensus popular', 'ai consensus rank', 'ai consensus accuracy'].map(modelKey => {
              const pick = consensus.find(p => p.model.toLowerCase() === modelKey)
              const labelMap = {
                'ai consensus popular': 'Most Popular',
                'ai consensus rank': 'Rank Weighted',
                'ai consensus accuracy': 'Accuracy Weighted',
              }
              return (
                <div key={modelKey} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', minWidth: 140 }}>
                    {labelMap[modelKey]}
                  </span>
                  {pick ? (
                    <>
                      <span style={{ fontFamily: 'var(--mono)', fontWeight: 600, fontSize: 13, color: 'var(--olive)' }}>
                        {pick.ticker}
                      </span>
                      <span style={{
                        fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600,
                        color: pick.direction === 'up' ? 'var(--green)' : 'var(--red)',
                        background: pick.direction === 'up' ? 'var(--green-bg)' : 'var(--red-bg)',
                        padding: '2px 8px', borderRadius: 3,
                      }}>
                        {pick.direction === 'up' ? '↑ UP' : '↓ DOWN'}
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>No Pick</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="dash-tabs" style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 0, overflowX: 'auto' }}>
        {VIEWS.map(v => (
          <button key={v.key} onClick={() => setView(v.key)} style={{
            padding: '10px 20px', background: 'none', border: 'none',
            borderBottom: view === v.key ? '2px solid var(--olive)' : '2px solid transparent',
            color: view === v.key ? 'var(--olive)' : 'var(--text3)',
            fontWeight: view === v.key ? 600 : 400,
            fontSize: 13, cursor: 'pointer', fontFamily: 'var(--sans)',
            marginBottom: -1, transition: 'all 0.15s', whiteSpace: 'nowrap', flexShrink: 0,
          }}>{v.label}</button>
        ))}
      </div>

      {/* Table wrapper — horizontally scrollable on mobile */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
        <div className="dash-table-wrap">
          {loading ? (
            <div>
              <div style={{ padding: '20px 16px 12px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--border)', background: 'var(--surface2)' }}>
                <div style={{ width: 16, height: 16, border: '2px solid var(--olive-dim)', borderTopColor: 'var(--olive)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>Fetching earnings data...</span>
              </div>
              {[...Array(8)].map((_, i) => (
                <div key={i} style={{ height: 48, display: 'flex', alignItems: 'center', gap: 16, padding: '0 16px', borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface2)' }}>
                  <div style={{ width: 52, height: 14, borderRadius: 3, background: 'var(--surface3)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ width: 160, height: 14, borderRadius: 3, background: 'var(--surface3)', animation: 'pulse 1.5s ease-in-out infinite 0.1s' }} />
                  <div style={{ width: 70, height: 14, borderRadius: 3, background: 'var(--surface3)', animation: 'pulse 1.5s ease-in-out infinite 0.2s', marginLeft: 'auto' }} />
                  <div style={{ width: 80, height: 22, borderRadius: 3, background: 'var(--surface3)', animation: 'pulse 1.5s ease-in-out infinite 0.3s' }} />
                </div>
              ))}
            </div>
          ) : (
            <table className="dash-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
              <thead>
                <tr style={{ background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {[
                    { key: 'ticker', label: 'Ticker', cls: '' },
                    { key: 'name', label: 'Company', cls: '' },
                    { key: 'price', label: 'Price', cls: '' },
                    { key: 'change', label: 'Change %', cls: '' },
                    { key: 'mkt_cap', label: 'Mkt Cap', cls: 'col-mktcap' },
                    { key: 'report_time', label: 'Session', cls: 'col-session' },
                  ].map(col => (
                    <th key={col.key} className={col.cls} onClick={() => handleSort(col.key)} style={{
                      padding: '11px 16px', textAlign: 'left',
                      fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                      letterSpacing: '1px', color: 'var(--text3)',
                      cursor: 'pointer', userSelect: 'none',
                      fontFamily: 'var(--mono)', whiteSpace: 'nowrap',
                    }}>
                      {col.label}<SortArrow col={col.key} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{
                      padding: '60px 16px', textAlign: 'center',
                      color: 'var(--text3)', fontSize: 13, fontFamily: 'var(--mono)',
                    }}>
                      — No earnings data for this window —
                    </td>
                  </tr>
                ) : sorted.map((s, i) => (
                  <tr key={s.ticker} style={{
                    borderBottom: '1px solid var(--border)',
                    background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface2)',
                  }}>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontFamily: 'var(--mono)', fontWeight: 500, fontSize: 13, color: 'var(--olive)', letterSpacing: '0.5px' }}>
                        {s.ticker}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text)', fontWeight: 400, minWidth: 130 }}>
                      {s.name}
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                      {s.show_dash ? <span style={{ color: 'var(--text3)' }}>—</span> : s.price != null ? `$${s.price.toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      {s.show_dash || s.change == null ? (
                        <span style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 13 }}>—</span>
                      ) : (
                        <span style={{
                          fontFamily: 'var(--mono)', fontWeight: 500, fontSize: 13,
                          color: s.change >= 0 ? 'var(--green)' : 'var(--red)',
                          background: s.change >= 0 ? 'var(--green-bg)' : 'var(--red-bg)',
                          padding: '2px 8px', borderRadius: 3,
                        }}>
                          {s.change >= 0 ? '+' : ''}{s.change.toFixed(2)}%
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', whiteSpace: 'nowrap' }}>
                      {s.mkt_cap || '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                        letterSpacing: '0.5px', fontFamily: 'var(--mono)',
                        color: s.report_time === 'pre' ? '#8b6914' : '#4a6741',
                        background: s.report_time === 'pre' ? 'rgba(139,105,20,0.08)' : 'rgba(74,103,65,0.08)',
                        padding: '3px 8px', borderRadius: 2,
                      }}>
                        {s.report_time === 'pre' ? 'AM' : 'PM'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
